import React, { memo, useEffect, useState } from "react";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  FormGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { triggerLoader } from "../../../redux/reducers/LoaderTrigger";
import configServ from "../../../services/config";
import AlertDialog from "../../AlertDialog/AlertDialog";

function AddHsnDialog({ open, handleClose, onHsnAdded }) {
  const dispatch = useDispatch();
  const { company_id } = useSelector((state) => state.GlobalVariables);

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const initialFormData = {
    countrycode: "91",
    hsncode: "",
    type: "hsn",
    GSTRate: "",
    IGSTRate: "",
    CGSTRate: "",
    SGSTRate: "",
    EffectiveDate: getTodayDate(),
    CESS: "",
    RateRevision: "",
    HSNdescription: "",
    ActiveFlage: true,
    isPrimary: false,
  };

  const [formData, setFormData] = useState(initialFormData);
  const [validationErrors, setValidationErrors] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState("Alert");
  const [dialogContent, setDialogContent] = useState("");
  const [buttonType, setButtonType] = useState(null);

  const handleOpenDialog = (heading, content, type) => {
    setTitle(heading);
    setDialogContent(content);
    setButtonType(type);
    setDialogOpen(true);
  };

  const handleCloseAlertDialog = () => {
    setDialogOpen(false);
    setTitle("Alert");
    setDialogContent("");
    setButtonType(null);
  };

  const clear = () => {
    setFormData({
      ...initialFormData,
      EffectiveDate: getTodayDate(),
    });
    setValidationErrors({});
  };

  const handleModalClose = () => {
    clear();
    handleClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "GSTRate") {
      const numVal = parseFloat(value);
      if (!isNaN(numVal)) {
        const half = (numVal / 2).toString();
        setFormData((state) => ({
          ...state,
          GSTRate: value,
          IGSTRate: value,
          CGSTRate: half,
          SGSTRate: half,
        }));
      } else {
        setFormData((state) => ({
          ...state,
          GSTRate: value,
          IGSTRate: value,
          CGSTRate: "",
          SGSTRate: "",
        }));
      }
    } else {
      setFormData((state) => ({
        ...state,
        [name]: value,
      }));
    }

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleChangeCheckbox = (e) => {
    const { name, checked } = e.target;
    setFormData((state) => ({
      ...state,
      [name]: checked,
    }));
  };

  useEffect(() => {
    if (open) {
      clear();
    }
  }, [open]);

  const validate = () => {
    const errors = {};
    if (!formData.countrycode) {
      errors.countrycode = "required";
    }
    if (!formData.hsncode) {
      errors.hsncode = "required";
    }
    if (!formData.type) {
      errors.type = "required";
    }
    if (formData.GSTRate === "" || formData.GSTRate === undefined) {
      errors.GSTRate = "required";
    }
    if (formData.IGSTRate === "" || formData.IGSTRate === undefined) {
      errors.IGSTRate = "required";
    }
    if (formData.CGSTRate === "" || formData.CGSTRate === undefined) {
      errors.CGSTRate = "required";
    }
    if (formData.SGSTRate === "" || formData.SGSTRate === undefined) {
      errors.SGSTRate = "required";
    }
    if (!formData.EffectiveDate) {
      errors.EffectiveDate = "required";
    }

    setValidationErrors(errors);
    return Object.values(errors).every((error) => error === "");
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    try {
      const isValid = validate();
      if (!isValid) {
        return false;
      }

      dispatch(triggerLoader());
      const dataToSend = {
        ...formData,
        company_id: company_id,
      };

      const res = await configServ.addHsn(dataToSend);
      if (res.status === 200) {
        clear();
        if (onHsnAdded) {
          onHsnAdded(res.data);
        }
        handleClose();
      } else if (res.status === 400) {
        handleOpenDialog(
          "Warning",
          res.message || "Failed to create HSN.",
          "warning"
        );
      } else {
        handleOpenDialog(
          "Error",
          res.message || "Failed to create HSN.",
          "error"
        );
      }
    } catch (e) {
      console.log(e);
      handleOpenDialog("Error", "An unexpected error has occurred.", "error");
    } finally {
      dispatch(triggerLoader());
    }
  };

  return (
    <>
      <AlertDialog
        open={dialogOpen}
        setOpen={handleCloseAlertDialog}
        title={title}
        message={dialogContent}
        buttonTitle="Ok"
        buttonType={buttonType}
      />

      <Dialog
        open={open}
        onClose={handleModalClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ backgroundColor: "#e3f3fc", py: 1.5 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight="bold" color="#1e61b4">
              Add New HSN
            </Typography>
            <IconButton onClick={handleModalClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ backgroundColor: "#f9fbfe", py: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                sx={{ fontSize: "16px", fontWeight: "bold", color: "#1e61b4" }}
              >
                HSN Detail
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                id="countrycode"
                fullWidth
                size="small"
                sx={{ backgroundColor: "white" }}
                label={
                  <span>
                    Country Code<span style={{ color: "red" }}>*</span>
                  </span>
                }
                name="countrycode"
                onChange={handleChange}
                error={!!validationErrors.countrycode}
                value={formData.countrycode || ""}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                id="hsncode"
                type="number"
                fullWidth
                size="small"
                sx={{ backgroundColor: "white" }}
                label={
                  <span>
                    HSN/SAC<span style={{ color: "red" }}>*</span>
                  </span>
                }
                name="hsncode"
                onChange={handleChange}
                error={!!validationErrors.hsncode}
                value={formData.hsncode || ""}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="type-label">
                  Type<span style={{ color: "red" }}>*</span>
                </InputLabel>
                <Select
                  fullWidth
                  size="small"
                  id="type"
                  name="type"
                  value={formData.type}
                  label="Type*"
                  onChange={handleChange}
                  sx={{ backgroundColor: "white" }}
                  error={!!validationErrors.type}
                >
                  <MenuItem value="hsn">HSN</MenuItem>
                  <MenuItem value="sac">SAC</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                id="GSTRate"
                type="number"
                fullWidth
                size="small"
                sx={{ backgroundColor: "white" }}
                label={
                  <span>
                    GST Rate(%)<span style={{ color: "red" }}>*</span>
                  </span>
                }
                name="GSTRate"
                onChange={handleChange}
                error={!!validationErrors.GSTRate}
                value={formData.GSTRate ?? ""}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                id="IGSTRate"
                type="number"
                fullWidth
                size="small"
                sx={{ backgroundColor: "white" }}
                label={
                  <span>
                    IGST Rate(%)<span style={{ color: "red" }}>*</span>
                  </span>
                }
                name="IGSTRate"
                onChange={handleChange}
                error={!!validationErrors.IGSTRate}
                value={formData.IGSTRate ?? ""}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                id="CGSTRate"
                type="number"
                fullWidth
                size="small"
                sx={{ backgroundColor: "white" }}
                label={
                  <span>
                    CGST Rate(%)<span style={{ color: "red" }}>*</span>
                  </span>
                }
                name="CGSTRate"
                onChange={handleChange}
                error={!!validationErrors.CGSTRate}
                value={formData.CGSTRate ?? ""}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                id="SGSTRate"
                type="number"
                fullWidth
                size="small"
                sx={{ backgroundColor: "white" }}
                label={
                  <span>
                    SGST Rate(%)<span style={{ color: "red" }}>*</span>
                  </span>
                }
                name="SGSTRate"
                onChange={handleChange}
                error={!!validationErrors.SGSTRate}
                value={formData.SGSTRate ?? ""}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                id="EffectiveDate"
                type="date"
                fullWidth
                size="small"
                sx={{ backgroundColor: "white" }}
                label={
                  <span>
                    Effective Date<span style={{ color: "red" }}>*</span>
                  </span>
                }
                name="EffectiveDate"
                onChange={handleChange}
                error={!!validationErrors.EffectiveDate}
                value={formData.EffectiveDate || ""}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                id="CESS"
                fullWidth
                size="small"
                sx={{ backgroundColor: "white" }}
                label="CESS"
                name="CESS"
                onChange={handleChange}
                value={formData.CESS || ""}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                id="RateRevision"
                fullWidth
                size="small"
                sx={{ backgroundColor: "white" }}
                label="Rate Revision"
                name="RateRevision"
                onChange={handleChange}
                value={formData.RateRevision || ""}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                id="HSNdescription"
                fullWidth
                size="small"
                sx={{ backgroundColor: "white" }}
                label="HSN Description"
                name="HSNdescription"
                onChange={handleChange}
                value={formData.HSNdescription || ""}
                variant="outlined"
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12}>
              <FormGroup sx={{ display: "flex", flexDirection: "row" }}>
                <FormControlLabel
                  name="ActiveFlage"
                  control={
                    <Checkbox checked={Boolean(formData.ActiveFlage) || false} />
                  }
                  onChange={handleChangeCheckbox}
                  label="Status"
                  style={{ marginRight: "20px" }}
                />
                <FormControlLabel
                  name="isPrimary"
                  control={
                    <Checkbox checked={Boolean(formData.isPrimary) || false} />
                  }
                  onChange={handleChangeCheckbox}
                  label="Is Primary"
                />
              </FormGroup>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ backgroundColor: "#e3f3fc", px: 3, py: 1.5 }}>
          <Button
            onClick={clear}
            variant="contained"
            color="secondary"
            startIcon={<RotateLeftIcon />}
            sx={{ mx: 1 }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            startIcon={<SaveIcon />}
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default memo(AddHsnDialog);
